import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Outlet } from 'react-router-dom';

// Layout Components
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
const AdminLayout = lazy(() => import('./components/layout/AdminLayout'));
const CustomerLayout = lazy(() => import('./components/layout/CustomerLayout'));
const TechnicianLayout = lazy(() => import('./components/layout/TechnicianLayout'));

// Core Public - Eagerly loaded for instantaneous homepage rendering
import HomePage from './pages/public/HomePage';

// Lazy Loaded Public Pages (Chunked on-demand)
const ServicesPage = lazy(() => import('./pages/public/ServicesPage'));
const ServiceDetailPage = lazy(() => import('./pages/public/ServiceDetailPage'));
const LocalityDetailPage = lazy(() => import('./pages/public/LocalityDetailPage'));
const AboutPage = lazy(() => import('./pages/public/AboutPage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));
const FaqPage = lazy(() => import('./pages/public/FaqPage'));
const BlogPage = lazy(() => import('./pages/public/BlogPage'));
const BlogDetailPage = lazy(() => import('./pages/public/BlogDetailPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/public/PrivacyPolicyPage'));
const TermsPage = lazy(() => import('./pages/public/TermsPage'));
const WarrantyPolicyPage = lazy(() => import('./pages/public/WarrantyPolicyPage'));
const RefundPolicyPage = lazy(() => import('./pages/public/RefundPolicyPage'));
const DisclaimerPage = lazy(() => import('./pages/public/DisclaimerPage'));
const NotFoundPage = lazy(() => import('./pages/public/NotFoundPage'));
const ScrollManager = lazy(() => import('./components/layout/ScrollManager'));
const WorksPage = lazy(() => import('./pages/public/WorksPage'));
const WorkDetailPage = lazy(() => import('./pages/public/WorkDetailPage'));
const ProcessPage = lazy(() => import('./pages/public/ProcessPage'));
const ShutterTypesPage = lazy(() => import('./pages/public/ShutterTypesPage'));
const AreasPage = lazy(() => import('./pages/public/AreasPage'));
const OurClientsPage = lazy(() => import('./pages/public/OurClientsPage'));
const FloatingChatWidget = lazy(() => import('./components/common/FloatingChatWidget'));

// Technician Public & Partner Pages (Lazy)
const JoinTechnicianPage = lazy(() => import('./pages/technician/JoinTechnicianPage'));
const TechnicianEarningsPage = lazy(() => import('./pages/technician/TechnicianEarningsPage'));
const TechnicianBenefitsPage = lazy(() => import('./pages/technician/TechnicianBenefitsPage'));
const TechnicianGuidelinesPage = lazy(() => import('./pages/technician/TechnicianGuidelinesPage'));

// Authentication Pages (Completely Separated Portals) (Lazy)
const CustomerLoginPage = lazy(() => import('./pages/auth/CustomerLoginPage'));
const CustomerRegisterPage = lazy(() => import('./pages/auth/CustomerRegisterPage'));
const CustomerForgotPasswordPage = lazy(() => import('./pages/auth/CustomerForgotPasswordPage'));
const TechnicianLoginPage = lazy(() => import('./pages/auth/TechnicianLoginPage'));
const TechnicianRegisterPage = lazy(() => import('./pages/auth/TechnicianRegisterPage'));
const TechnicianForgotPasswordPage = lazy(() => import('./pages/auth/TechnicianForgotPasswordPage'));
const AdminLoginPage = lazy(() => import('./pages/auth/AdminLoginPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));

// Auth State Provider & Route Guards
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import SessionTimeoutHandler from './components/common/SessionTimeoutHandler';
import RequireRole from './components/auth/RequireRole';
import ErrorBoundary from './components/common/ErrorBoundary';

// Portal Dashboards (Lazy)
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const CustomerDashboard = lazy(() => import('./pages/customer/CustomerDashboard'));
const TechnicianDashboard = lazy(() => import('./pages/technician/TechnicianDashboard'));

// Public Website Shell (Header + Content + Footer)
function PublicShell() {
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      <main className="flex-1">
        <ErrorBoundary>
          <Suspense fallback={<div className="min-h-[50vh] bg-white flex items-center justify-center"><div className="w-8 h-8 border-2 border-[#00796b] border-t-transparent rounded-full animate-spin"></div></div>}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </main>
      <Footer />
    </div>
  );
}

export default function App() {
  // Dynamically detect local XAMPP subpath vs Hostinger production root
  const isXampp = window.location.pathname.startsWith('/shutterservices');
  const basename = isXampp ? '/shutterservices' : '/';

  return (
    <BrowserRouter basename={basename}>
      <ToastProvider>
        <AuthProvider>
          <SessionTimeoutHandler />
          <Suspense fallback={null}>
            <ScrollManager />
            <FloatingChatWidget />
          </Suspense>
          <ErrorBoundary>
            <Suspense fallback={<div className="min-h-screen bg-white" />}>
              <Routes>
              {/* Public Website Routes */}
              <Route element={<PublicShell />}>
                <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/about/" element={<AboutPage />} />
          <Route path="/about-us" element={<AboutPage />} />
          <Route path="/about-us/" element={<AboutPage />} />
          <Route path="/our-services" element={<ServicesPage />} />
          <Route path="/our-services/" element={<ServicesPage />} />
          <Route path="/contact-us" element={<ContactPage />} />
          <Route path="/contact-us/" element={<ContactPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/contact/" element={<ContactPage />} />
          <Route path="/faq" element={<FaqPage />} />
          <Route path="/faq/" element={<FaqPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogDetailPage />} />
          <Route path="/blog/:slug/" element={<BlogDetailPage />} />
          <Route path="/our-works" element={<WorksPage />} />
          <Route path="/our-works/" element={<WorksPage />} />
          <Route path="/our-works/:slug" element={<WorkDetailPage />} />
          <Route path="/our-works/:slug/" element={<WorkDetailPage />} />
          <Route path="/work/:slug" element={<WorkDetailPage />} />
          <Route path="/work/:slug/" element={<WorkDetailPage />} />
          <Route path="/project/:slug" element={<WorkDetailPage />} />
          <Route path="/project/:slug/" element={<WorkDetailPage />} />
          <Route path="/latest-projects" element={<WorksPage />} />
          <Route path="/latest-projects/" element={<WorksPage />} />
          <Route path="/how-it-works" element={<ProcessPage />} />
          <Route path="/how-it-works/" element={<ProcessPage />} />
          <Route path="/service-process" element={<ProcessPage />} />
          <Route path="/service-process/" element={<ProcessPage />} />
          <Route path="/shutter-types" element={<ShutterTypesPage />} />
          <Route path="/shutter-types/" element={<ShutterTypesPage />} />
          <Route path="/our-clients" element={<OurClientsPage />} />
          <Route path="/our-clients/" element={<OurClientsPage />} />
          <Route path="/clients" element={<OurClientsPage />} />
          <Route path="/clients/" element={<OurClientsPage />} />
          <Route path="/service-areas" element={<AreasPage />} />
          <Route path="/service-areas/" element={<AreasPage />} />
          <Route path="/rolling-shutter-service-across-hyderabad" element={<AreasPage />} />
          <Route path="/rolling-shutter-service-across-hyderabad/" element={<AreasPage />} />
          <Route path="/hyderabad" element={<AreasPage />} />
          <Route path="/hyderabad/" element={<AreasPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/privacy-policy/" element={<PrivacyPolicyPage />} />
          <Route path="/terms-conditions" element={<TermsPage />} />
          <Route path="/terms-conditions/" element={<TermsPage />} />
          <Route path="/warranty-policy" element={<WarrantyPolicyPage />} />
          <Route path="/warranty-policy/" element={<WarrantyPolicyPage />} />
          <Route path="/refund-policy" element={<RefundPolicyPage />} />
          <Route path="/refund-policy/" element={<RefundPolicyPage />} />
          <Route path="/disclaimer" element={<DisclaimerPage />} />
          <Route path="/disclaimer/" element={<DisclaimerPage />} />

          {/* Technician Partner Information Pages */}
          <Route path="/technician/join" element={<JoinTechnicianPage />} />
          <Route path="/technician/join/" element={<JoinTechnicianPage />} />
          <Route path="/join-as-technician" element={<JoinTechnicianPage />} />
          <Route path="/join-as-technician/" element={<JoinTechnicianPage />} />
          <Route path="/technician/earnings" element={<TechnicianEarningsPage />} />
          <Route path="/technician/earnings/" element={<TechnicianEarningsPage />} />
          <Route path="/technician/commissions" element={<TechnicianEarningsPage />} />
          <Route path="/technician/commissions/" element={<TechnicianEarningsPage />} />
          <Route path="/technician/benefits" element={<TechnicianBenefitsPage />} />
          <Route path="/technician/benefits/" element={<TechnicianBenefitsPage />} />
          <Route path="/technician/guidelines" element={<TechnicianGuidelinesPage />} />
          <Route path="/technician/guidelines/" element={<TechnicianGuidelinesPage />} />

          {/* Service Detail Pages */}
          <Route path="/service/:slug" element={<ServiceDetailPage />} />
          <Route path="/service/:slug/" element={<ServiceDetailPage />} />
          <Route path="/services/:slug" element={<ServiceDetailPage />} />
          <Route path="/services/:slug/" element={<ServiceDetailPage />} />

          {/* Locality Pages (e.g. /rolling-shutter-repair-in-gachibowli-premier-shutter-service/) */}
          <Route path="/:slug" element={<LocalityDetailPage />} />
          <Route path="/:slug/" element={<LocalityDetailPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>

        {/* Separated Customer Authentication Routes */}
        <Route path="/login" element={<CustomerLoginPage />} />
        <Route path="/customer/login" element={<CustomerLoginPage />} />
        <Route path="/customer/register" element={<CustomerRegisterPage />} />
        <Route path="/customer/forgot-password" element={<CustomerForgotPasswordPage />} />

        {/* Separated Field Technician Mobile Login & Registration */}
        <Route path="/technician/login" element={<TechnicianLoginPage />} />
        <Route path="/technician/login/" element={<TechnicianLoginPage />} />
        <Route path="/technician/register" element={<TechnicianRegisterPage />} />
        <Route path="/technician/register/" element={<TechnicianRegisterPage />} />
        <Route path="/technician/forgot-password" element={<TechnicianForgotPasswordPage />} />
        <Route path="/technician/forgot-password/" element={<TechnicianForgotPasswordPage />} />
        {/* Password Reset Routes */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/reset-password/" element={<ResetPasswordPage />} />
        <Route path="/customer/reset-password" element={<ResetPasswordPage />} />
        <Route path="/technician/reset-password" element={<ResetPasswordPage />} />
        <Route path="/technician/reset" element={<ResetPasswordPage />} />

        {/* Separated Administrative CRM Login (Isolated) */}
        <Route path="/admin/login" element={<AdminLoginPage />} />

        {/* Administrative CRM Hub */}
        <Route 
          path="/admin" 
          element={
            <RequireRole allowedRoles={['admin', 'super_admin', 'staff']} loginPath="/admin/login">
              <AdminLayout />
            </RequireRole>
          }
        >
          <Route index element={<AdminDashboard />} />
          <Route path="leads" element={<AdminDashboard />} />
          <Route path="customers" element={<AdminDashboard />} />
          <Route path="quotations" element={<AdminDashboard />} />
          <Route path="work-orders" element={<AdminDashboard />} />
          <Route path="invoices" element={<AdminDashboard />} />
          <Route path="technicians" element={<AdminDashboard />} />
          <Route path="inventory" element={<AdminDashboard />} />
          <Route path="tickets" element={<AdminDashboard />} />
          <Route path="warranties" element={<AdminDashboard />} />
          <Route path="audit-logs" element={<AdminDashboard />} />
          <Route path="reports" element={<AdminDashboard />} />
          <Route path="settings" element={<AdminDashboard />} />
        </Route>

        {/* Customer Self-Service Portal (/client & backwards-compatible /my-account) */}
        <Route 
          path="/client" 
          element={
            <RequireRole allowedRoles={['customer']} loginPath="/customer/login">
              <CustomerLayout />
            </RequireRole>
          }
        >
          <Route index element={<CustomerDashboard />} />
          <Route path="*" element={<CustomerDashboard />} />
        </Route>

        <Route 
          path="/my-account" 
          element={
            <RequireRole allowedRoles={['customer']} loginPath="/customer/login">
              <CustomerLayout />
            </RequireRole>
          }
        >
          <Route index element={<CustomerDashboard />} />
          <Route path="*" element={<CustomerDashboard />} />
        </Route>

        {/* Field Technician Mobile App */}
        <Route 
          path="/technician" 
          element={
            <RequireRole allowedRoles={['technician']} loginPath="/technician/login">
              <TechnicianLayout />
            </RequireRole>
          }
        >
          <Route index element={<TechnicianDashboard />} />
          <Route path="*" element={<TechnicianDashboard />} />
        </Route>
      </Routes>
    </Suspense>
  </ErrorBoundary>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
